import { useEffect } from 'react'

import { useAppDispatch } from '@/redux/hooks'
import { AssistantActions } from '@/redux/reducers/assistant'
import { SettingsActions } from '@/redux/reducers/settings'

/**
 * Points the app-shell chat at an errand for as long as its page is open, and lets go on unmount.
 *
 * The chat is mounted by the shell rather than the errand page, so which errand and thread it is
 * showing travels through redux. Both of its realtime subscriptions are keyed on those values,
 * which is why this is deliberately keyed on the errand's ID and not on the fetched errand object:
 * re-running it on every errands refetch cleared and restored the ids, and a proactive reaction
 * landing inside one of those gaps was swallowed by the resubscribe.
 *
 * Split in two on purpose. Binding the ids has to follow the thread, which resolves a moment
 * after mount; opening the chat must NOT, or the user cannot close it.
 */
export const useErrandChatBinding = (params: { errandId?: string; threadId?: string }) => {
  const { errandId, threadId } = params
  const dispatch = useAppDispatch()

  // Which errand and thread the shell's chat is pointed at. Re-runs when the thread arrives.
  useEffect(() => {
    if (!errandId) return

    dispatch(SettingsActions.setSelectedErrandId(errandId))
    dispatch(AssistantActions.setActiveThreadId(threadId))

    return () => {
      dispatch(AssistantActions.setActiveThreadId(undefined))
      dispatch(SettingsActions.setSelectedErrandId(undefined))
    }
  }, [dispatch, errandId, threadId])

  /**
   * Opening the chat, keyed on the ERRAND alone.
   *
   * This used to depend on `isSideChatOpen` as well, so that anything closing the chat was
   * undone — including the user. Pressing "Dölj chatt" set it false, the dependency changed,
   * the effect re-ran and opened it again, so the button had never worked on an errand page.
   * On desktop that reads as a control that snaps back; on mobile the chat covers the whole
   * screen (`chatTakesFullScreen` in the app shell), so it was a trap with no way back to the
   * errand.
   *
   * Keyed on the errand also means the chat does not reappear when the thread id resolves a
   * second after mount, which would otherwise reopen it under anyone quick enough to close it
   * first.
   */
  useEffect(() => {
    if (!errandId) return

    dispatch(AssistantActions.toggleSideChat(true))

    return () => {
      dispatch(AssistantActions.toggleSideChat(false))
    }
  }, [dispatch, errandId])
}
